import React from 'react';
import { StyleSheet, View } from './components/base_components';

import { BusyOverlay, useBusy } from './components/busy_component';
import Breadcrumb from './components/breadcrumb';
import BoxPropList from './components/box_prop_list';
import ProgressSpinner from './components/progress_spinner';
import OrgTabBar from './components/org_tab_bar';

import OrgStore from './stores/org_store';
import UserStore from './stores/user_store';

const SUFFIX = process.env.CUSTOM_DOMAIN_SUFFIX;

const styles = StyleSheet.create({
  orgScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  spinner: {
    margin: '3rem',
    alignSelf: 'center',
  },
  tabBar: {
    marginLeft: '2.2rem',
    marginRight: '2.2rem',
  },
});

export default function OrgScreen() {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const org_id = UserStore.useOrg()?.org_id;
  const org = OrgStore.useOrg(org_id);
  const login_custom_domain = UserStore.getCustomDomain();
  const [updates, setUpdates] = React.useState<any>({});
  const custom_domain = `https://${org?.domain_prefix}${SUFFIX}`;

  function _setUpdates(new_updates) {
    if (new_updates.domain_prefix) {
      new_updates.domain_prefix = new_updates.domain_prefix.toLowerCase();
    }
    setUpdates(new_updates);
  }

  React.useEffect(() => {
    OrgStore.fetch(org_id);
  }, [org_id]);

  function _onSave(complete) {
    if (setBusy()) {
      const opts: any = { org_id };
      if (updates.org_name !== undefined) {
        opts.org_name = updates.org_name;
      }
      if (updates.domain_prefix !== undefined) {
        opts.domain_prefix = updates.domain_prefix;
      }
      OrgStore.updateOrg(opts, (err) => {
        clearBusy();
        if (err === 409) {
          window.alert('Name in use, please try another.');
        } else if (err === 412) {
          window.alert('Name reserved, please try another.');
        } else if (err === 400) {
          window.alert(
            'Invalid domain prefix, please use a-z, 0-9, dash and underscore only.'
          );
        } else if (err) {
          window.alert('Failed to update org.');
        } else {
          complete?.();
          if (opts.domain_prefix !== undefined && login_custom_domain) {
            if (opts.domain_prefix) {
              window.alert(
                'Org saved, you will be redirected to the new domain to login.'
              );
              const url = `https://${opts.domain_prefix}${SUFFIX}`;
              window.location.href = url;
            } else {
              window.alert(
                'Org saved and custom domain removed. You will be redirected to Maxlo login.'
              );
              window.location.href = process.env.BASE_URL ?? '';
            }
          }
        }
      });
    }
  }
  let content: any = null;
  if (!org) {
    content = <ProgressSpinner style={styles.spinner} color='#555' />;
  } else {
    content = (
      <>
        <Breadcrumb list={[org?.org_name]} />
        <OrgTabBar style={styles.tabBar} />
        <BoxPropList
          title='Organization Details'
          editable={true}
          updates={updates}
          onChange={_setUpdates}
          onSave={_onSave}
          columns={[
            [
              {
                key: 'org_name',
                label: 'Organization Name',
                value: org?.org_name,
                input: {
                  placeholder: 'org name',
                },
              },
              {
                key: 'domain_prefix',
                label: 'Custom Domain',
                value: org?.domain_prefix,
                valuePrefix: 'https://',
                valueSuffix: SUFFIX,
                url: org?.domain_prefix ? custom_domain : null,
                emptyValue: '-',
                input: {
                  placeholder: 'domain',
                  suffix: SUFFIX,
                },
              },
            ],
          ]}
        />
      </>
    );
  }
  return (
    <View style={styles.orgScreen}>
      {content}
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
